import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Formik } from 'formik';
import DriverService from '../../services/driver/driver-service';
import { RegenerateLetterSchema } from '../../validation/driver-debt/driver-debt-validation';
import { SelectBox } from '../../components/reactstrap-formik';
import { IDriverDebtorLetters } from '../../models/driver';
import { IRegenerateLetterModalForm } from '../../models/driver-debt';
import StandardResponse from '../../models/standard-response';
import { IRegenerateLetterModalProps } from '../../types/propTypes';
import { handleErrorMessage } from '../../utils/helper';

const RegenerateLetterModal = (props: IRegenerateLetterModalProps) => {
    const { t } = useTranslation();

    const initialValues: IRegenerateLetterModalForm = {
        debtorLetter: null,
    };

    const [debtLetters, setDebtLetters] = React.useState<Array<IDriverDebtorLetters>>([]);

    const driverService = new DriverService();

    const fetchData = async () => {
        const { runHistoryDetails, s1Letters, s2Letters } = props;
        let debtLetters: IDriverDebtorLetters[] | null = [];
        if (runHistoryDetails.chase_report_type && runHistoryDetails.chase_report_type.code === 'STRIKE_ONE') {
            debtLetters = s1Letters;
        } else if (runHistoryDetails.chase_report_type && runHistoryDetails.chase_report_type.code === 'STRIKE_TWO') {
            debtLetters = s2Letters;
        }
        setDebtLetters(debtLetters);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSubmit = async (values: IRegenerateLetterModalForm) => {
        const { runHistoryDetails } = props;
        const inputData = {
            template_id: values.debtorLetter.id,
            chase_report_data_id: runHistoryDetails.id,
        };
        await driverService
            .regenerateChaseReport(inputData)
            .then(async (res: StandardResponse<number>) => {
                toast.success(res.Messages);
                props.refreshPastLettersList();
                props.closeModal();
            })
            .catch((m) => {
                handleErrorMessage(m);
            });
    };

    const { showModal, closeModal, runHistoryDetails } = props;

    return (
        <Modal show={showModal} onHide={closeModal}>
            <Modal.Header closeButton>
                <Modal.Title>{t('driverDebt.label.regenerateLetters')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validateOnChange={false}
                    validateOnBlur={false}
                    validationSchema={RegenerateLetterSchema()}
                    onSubmit={(values: IRegenerateLetterModalForm) => handleSubmit(values)}
                >
                    {(props) => {
                        const { handleSubmit, setFieldTouched, setFieldValue, touched, errors, values } = props;
                        return (
                            <form
                                id="regenerateLetterForm"
                                onSubmit={handleSubmit}
                                noValidate
                                autoComplete="off"
                                className="mb-3"
                            >
                                <SelectBox
                                    name="debtorLetter"
                                    label={
                                        runHistoryDetails.chase_report_type &&
                                        runHistoryDetails.chase_report_type.code === 'STRIKE_ONE'
                                            ? t('driverDebt.button.strikeOneLetter')
                                            : t('driverDebt.button.strikeTwoLetter')
                                    }
                                    value={values.debtorLetter}
                                    options={debtLetters}
                                    isMulti={false}
                                    isInvalid={true}
                                    className={
                                        Boolean(touched['debtorLetter'] && errors['debtorLetter'])
                                            ? 'form-group is-invalid'
                                            : 'form-group'
                                    }
                                    onChange={(value) => {
                                        setFieldValue('debtorLetter', value !== null ? value : null);
                                    }}
                                    onBlur={() => setFieldTouched('debtorLetter', true)}
                                    placeholder={
                                        runHistoryDetails.chase_report_type &&
                                        runHistoryDetails.chase_report_type.code === 'STRIKE_ONE'
                                            ? t('driverDebt.placeholder.strikeOneLetter')
                                            : t('driverDebt.placeholder.strikeTwoLetter')
                                    }
                                    touched={touched.debtorLetter}
                                    getOptionLabel={(opt) => opt.name}
                                    getOptionValue={(opt) => opt.id}
                                    errors={errors.debtorLetter}
                                />
                                <div className="btn-block clearfix ModelBtn">
                                    <button
                                        type="submit"
                                        className="btn btn-primary mr-2 mb-2"
                                        title={t<string>('driverDebt.label.regenerate')}
                                    >
                                        <span>{t('driverDebt.label.regenerate')}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary mb-2"
                                        title={t<string>('driverDebt.label.cancel')}
                                        onClick={closeModal}
                                    >
                                        <span>{t('driverDebt.label.cancel')}</span>
                                    </button>
                                </div>
                            </form>
                        );
                    }}
                </Formik>
            </Modal.Body>
        </Modal>
    );
};
export default RegenerateLetterModal;
